import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
    XMarkIcon,
    KeyIcon,
    UserCircleIcon,
    CreditCardIcon,
    ArrowRightOnRectangleIcon,
    ShieldCheckIcon,
    CheckIcon,
    PhotoIcon,
    VideoIcon,
    FolderIcon,
    InfoIcon
} from './icons';
import { DriveFolderPicker } from './DriveFolderPicker';
import { setGeminiKey, clearGeminiKey } from '../utils/runtimeKeys';
import { ProviderKeys, getProviderKey, setProviderKey, clearProviderKey } from '../utils/providerKeys';

interface UserProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    // Current API Key state from Studio
    apiKey?: string | null;
    onSetKey: (key: string) => void;
    onClearKey: () => void;
    onResetOnboarding?: () => void;
    activeTab?: Tab;
    onTabChange?: (tab: Tab) => void;
}

type Tab = 'profile' | 'account' | 'integrations';

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
    isOpen,
    onClose,
    apiKey,
    onSetKey,
    onClearKey,
    onResetOnboarding,
    activeTab: externalActiveTab,
    onTabChange
}) => {
    const { user, signOut } = useAuth();
    const [internalActiveTab, setInternalActiveTab] = useState<Tab>('profile');

    const activeTab = externalActiveTab || internalActiveTab;
    const setActiveTab = (tab: Tab) => {
        if (onTabChange) {
            onTabChange(tab);
        } else {
            setInternalActiveTab(tab);
        }
    };

    // API Key State
    const [tempKey, setTempKey] = useState('');
    const [keyError, setKeyError] = useState<string | null>(null);

    // API Key State

    // LTX-2 / Fal.ai State (LocalStorage)
    const [falKey, setFalKey] = useState(() => getProviderKey(ProviderKeys.FAL_LTX) || '');
    const [falKeySaved, setFalKeySaved] = useState(() => !!getProviderKey(ProviderKeys.FAL_LTX));
    const [falKeyError, setFalKeyError] = useState<string | null>(null);

    // Google Drive Connection State
    const [driveConnected, setDriveConnected] = useState(false);
    const [driveLoading, setDriveLoading] = useState(false);
    const [driveFolders, setDriveFolders] = useState<{ id: string, name: string }[]>([]);
    const [selectedDriveFolder, setSelectedDriveFolder] = useState<string | null>(
        localStorage.getItem('jenial_drive_folder_id') || null
    );
    const [isFolderPickerOpen, setIsFolderPickerOpen] = useState(false);
    const [userId, setUserId] = useState<string | null>(null);

    // Check Drive connection on open
    useEffect(() => {
        if (isOpen && activeTab === 'integrations') {
            import('../services/supabaseClient').then(({ supabase }) => {
                supabase?.auth.getSession().then(({ data }) => {
                    setUserId(data.session?.user?.id || null);
                });
            });

            import('../services/googleDriveClient').then(async ({ isDriveConnected, listDriveFolders }) => {
                setDriveLoading(true);
                try {
                    const connected = await isDriveConnected();
                    setDriveConnected(connected);
                    if (connected) {
                        const folders = await listDriveFolders();
                        setDriveFolders(folders);
                    }
                } catch (e) {
                    console.warn('[Settings] Drive check failed:', e);
                } finally {
                    setDriveLoading(false);
                }
            });
        }
    }, [isOpen, activeTab]);

    // Save folder selection
    const handleSaveDriveFolder = () => {
        if (selectedDriveFolder) {
            localStorage.setItem('jenial_drive_folder_id', selectedDriveFolder);
        } else {
            localStorage.removeItem('jenial_drive_folder_id');
        }
    };

    useEffect(() => {
        if (isOpen) {
            setTempKey(apiKey || '');
        }
    }, [isOpen, apiKey]);

    // Handle ESC key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleSaveGeminiKey = () => {
        if (!tempKey.trim()) {
            onClearKey();
            setTempKey('');
            return;
        }

        if (!tempKey.startsWith('AIzaSy')) {
            setKeyError('Invalid Gemini Key. Must start with "AIzaSy"');
            return;
        }

        if (tempKey.length < 30) {
            setKeyError('Key seems too short.');
            return;
        }

        setGeminiKey(tempKey.trim());
        onSetKey(tempKey.trim());
        setKeyError(null);
    };

    const handleSaveFalKey = () => {
        if (!falKey.trim()) {
            clearProviderKey(ProviderKeys.FAL_LTX);
            setFalKeySaved(false);
            setFalKeyError(null);
            return;
        }

        // Validate Fal.ai key format (starts with fal_ or is a valid key)
        if (!falKey.startsWith('fal_') && falKey.length < 20) {
            setFalKeyError('La clé Fal.ai doit commencer par "fal_"');
            return;
        }

        setProviderKey(ProviderKeys.FAL_LTX, falKey.trim());
        setFalKeySaved(true);
        setFalKeyError(null);
    };

    const handleSaveOtherKeys = () => {
        handleSaveFalKey();
        // Close modal after saving
        onClose();
    };

    const renderTabs = () => (
        <div className="flex flex-col gap-1 w-56 border-r border-gray-700 p-3 bg-[#18181b]">
            <div className="px-2 mb-6">
                <h2 className="text-lg font-bold text-white tracking-tight">Paramètres</h2>
                <p className="text-xs text-gray-500">Compte & Préférences</p>
            </div>

            <button
                onClick={() => setActiveTab('profile')}
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg text-left transition-colors ${activeTab === 'profile' ? 'bg-indigo-600/20 text-indigo-300' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}
            >
                <UserCircleIcon className="w-5 h-5" />
                Profil
            </button>
            <button
                onClick={() => setActiveTab('account')}
                className={`flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg text-left transition-colors ${activeTab === 'account' ? 'bg-indigo-600/20 text-indigo-300' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`}
            >
                <CreditCardIcon className="w-5 h-5" />
                Compte & Plan
            </button>
            <button
                onClick={() => setActiveTab('integrations')}
                data-tour="settings-integrations-tab"
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'integrations'
                    ? 'bg-indigo-600/10 text-indigo-400 border border-indigo-500/20 shadow-[0_0_15px_rgba(79,70,229,0.1)]'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
            >
                <KeyIcon className="w-5 h-5" />
                Intégrations (API)
            </button>
        </div>
    );

    const renderProfileContent = () => (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
                <h3 className="text-xl font-bold text-white mb-2">Mon Profil</h3>
                <p className="text-sm text-gray-400">Gérez vos informations personnelles</p>
            </div>

            <div className="flex items-center gap-6 p-6 bg-gray-800/50 rounded-xl border border-gray-700">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg relative group cursor-pointer overflow-hidden ring-4 ring-gray-800">
                    {user?.user_metadata?.avatar_url ? (
                        <img src={user.user_metadata.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                        <span>JK</span>
                    )}
                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <PhotoIcon className="w-6 h-6 text-white" />
                    </div>
                </div>
                <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                    <div className="px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-gray-300 text-sm font-mono">
                        {user?.email || 'studio.user@example.com'}
                    </div>
                </div>
            </div>

            <div className="pt-6 border-t border-gray-700">
                <button
                    onClick={() => signOut()}
                    className="flex items-center gap-2 text-red-400 hover:text-red-300 text-sm font-medium transition-colors px-4 py-2 hover:bg-red-500/10 rounded-lg"
                >
                    <ArrowRightOnRectangleIcon className="w-5 h-5" />
                    Se déconnecter
                </button>
            </div>

            {/* Application Preferences */}
            <div className="pt-6 border-t border-gray-700">
                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Préférences</h4>
                <div className="bg-gray-800/30 p-4 rounded-xl border border-gray-700 space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium text-white text-sm">Guide de démarrage</p>
                            <p className="text-xs text-gray-500">Réafficher le tutoriel "Premiers Pas"</p>
                        </div>
                        <button
                            onClick={() => {
                                if (onResetOnboarding) {
                                    onResetOnboarding();
                                } else {
                                    localStorage.removeItem('studio_jenial_onboarding_seen_v2');
                                    window.location.reload();
                                }
                                onClose();
                            }}
                            className="bg-gray-700 hover:bg-gray-600 text-white text-xs px-3 py-1.5 rounded-lg transition-colors border border-gray-600"
                        >
                            Réinitialiser
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderAccountContent = () => (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
                <h3 className="text-xl font-bold text-white mb-2">Abonnement</h3>
                <p className="text-sm text-gray-400">Détails de votre plan actuel</p>
            </div>

            <div className="bg-gradient-to-r from-gray-900 to-gray-800 border border-gray-700 rounded-xl p-8 shadow-inner">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <span className="bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                            Freemium
                        </span>
                        <h4 className="text-2xl font-bold text-white mt-3">Plan Découverte</h4>
                        <p className="text-gray-400 text-sm mt-1">Idéal pour explorer la création vidéo IA</p>
                    </div>
                    {/* Placeholder for upgrade button */}
                    <button disabled className="px-5 py-2.5 bg-gray-700 text-gray-500 text-sm font-medium rounded-lg cursor-not-allowed border border-gray-600">
                        Gérer l'abonnement
                    </button>
                </div>
                <div className="space-y-3 pt-4 border-t border-gray-700/50">
                    <div className="flex items-center gap-3 text-sm text-gray-300">
                        <div className="p-1 bg-green-500/10 rounded-full">
                            <CheckIcon className="w-3.5 h-3.5 text-green-400" />
                        </div>
                        <span>Génération illimitée (BYOK - Bring Your Own Key)</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-300">
                        <div className="p-1 bg-green-500/10 rounded-full">
                            <CheckIcon className="w-3.5 h-3.5 text-green-400" />
                        </div>
                        <span>Stockage local sécurisé (IndexedDB)</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-300">
                        <div className="p-1 bg-green-500/10 rounded-full">
                            <CheckIcon className="w-3.5 h-3.5 text-green-400" />
                        </div>
                        <span>Exports HD 1080p</span>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderIntegrationsContent = () => (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
                <h3 className="text-xl font-bold text-white mb-2">Clés API & Fournisseurs</h3>
                <p className="text-sm text-gray-400">Connectez vos propres comptes pour générer du contenu</p>
            </div>

            {/* GEMINI SECTION */}
            <div className="bg-black/20 border border-gray-700 rounded-xl p-6 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <ShieldCheckIcon className="w-32 h-32 text-indigo-500" />
                </div>

                <div className="flex items-start gap-4 relative z-10">
                    <div className="p-3 bg-indigo-500/10 rounded-xl shrink-0 border border-indigo-500/20">
                        <ShieldCheckIcon className="w-8 h-8 text-indigo-400" />
                    </div>
                    <div className="flex-1 space-y-4">
                        <div>
                            <div className="flex items-center gap-2">
                                <h4 className="font-bold text-white text-lg">Google Gemini (Veo)</h4>
                            </div>
                            <p className="text-sm text-gray-400 mt-1">Génération vidéo & Assistant intelligent.</p>
                        </div>

                        <div className="space-y-3">
                            <div className="relative" data-tour="gemini-api-input">
                                <input
                                    type="password"
                                    value={tempKey}
                                    onChange={(e) => {
                                        setTempKey(e.target.value);
                                        setKeyError(null);
                                    }}
                                    placeholder="AIzaSy..."
                                    className={`w-full bg-[#121212] border ${keyError ? 'border-red-500' : 'border-gray-600'} rounded-lg pl-4 pr-10 py-3 text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all`}
                                />
                                {tempKey && (
                                    <button
                                        onClick={() => { setTempKey(''); onClearKey(); }}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white bg-gray-800 rounded-full p-0.5 transition-colors"
                                    >
                                        <XMarkIcon className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                            {keyError && (
                                <div className="flex items-center gap-2 text-red-400 text-xs pl-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                                    {keyError}
                                </div>
                            )}

                            <div className="flex justify-between items-center pt-2">
                                <a
                                    href="https://aistudio.google.com/app/api-keys"
                                    target="_blank"
                                    rel="noreferrer"
                                    data-tour="gemini-api-link"
                                    className="text-xs text-indigo-400 hover:text-indigo-300 hover:underline flex items-center gap-1"
                                >
                                    Obtenir une clé gratuite
                                    <ArrowRightOnRectangleIcon className="w-3 h-3" />
                                </a>
                                <button
                                    onClick={handleSaveGeminiKey}
                                    disabled={!tempKey || tempKey === apiKey}
                                    className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all ${apiKey && tempKey === apiKey
                                        ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                                        : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-500/20'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                    {apiKey && tempKey === apiKey ? (
                                        <span className="flex items-center gap-1.5">
                                            <CheckIcon className="w-3.5 h-3.5" />
                                            Connecté
                                        </span>
                                    ) : 'Sauvegarder la clé'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* FAL.AI / LTX-2 SECTION */}
            <div className="bg-black/20 border border-gray-700 rounded-xl p-6 relative overflow-hidden group mt-4">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <VideoIcon className="w-32 h-32 text-purple-500" />
                </div>

                <div className="flex items-start gap-4 relative z-10">
                    <div className="p-3 bg-purple-500/10 rounded-xl shrink-0 border border-purple-500/20">
                        <VideoIcon className="w-8 h-8 text-purple-400" />
                    </div>
                    <div className="flex-1 space-y-4">
                        <div>
                            <h4 className="font-bold text-white text-lg">Fal.ai (LTX-2)</h4>
                            <p className="text-sm text-gray-400 mt-1">
                                High-performance LTX-2 video generation.
                            </p>
                        </div>

                        <div className="space-y-3">
                            <div className="relative" data-tour="fal-api-input">
                                <input
                                    type="password"
                                    value={falKey}
                                    onChange={(e) => {
                                        setFalKey(e.target.value);
                                        setFalKeyError(null);
                                        // Reset saved state if key changed
                                        if (e.target.value !== getProviderKey(ProviderKeys.FAL_LTX)) {
                                            setFalKeySaved(false);
                                        }
                                    }}
                                    placeholder="fal_..."
                                    className={`w-full bg-[#121212] border ${falKeyError ? 'border-red-500' : 'border-gray-600'} rounded-lg pl-4 pr-10 py-3 text-sm text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all`}
                                />
                                {falKey && (
                                    <button
                                        onClick={() => { setFalKey(''); clearProviderKey(ProviderKeys.FAL_LTX); setFalKeySaved(false); }}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white bg-gray-800 rounded-full p-0.5 transition-colors"
                                    >
                                        <XMarkIcon className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                            {falKeyError && (
                                <div className="flex items-center gap-2 text-red-400 text-xs pl-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                                    {falKeyError}
                                </div>
                            )}

                            <div className="flex justify-between items-center pt-2">
                                <a
                                    href="https://fal.ai/dashboard/keys"
                                    target="_blank"
                                    rel="noreferrer"
                                    data-tour="fal-api-link"
                                    className="text-xs text-purple-400 hover:text-purple-300 hover:underline flex items-center gap-1"
                                >
                                    Obtenir une clé Fal.ai
                                    <ArrowRightOnRectangleIcon className="w-3 h-3" />
                                </a>
                                <button
                                    onClick={handleSaveFalKey}
                                    disabled={!falKey || (falKeySaved && falKey === getProviderKey(ProviderKeys.FAL_LTX))}
                                    className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all ${falKeySaved && falKey === getProviderKey(ProviderKeys.FAL_LTX)
                                        ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                                        : 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-500/20'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                    {falKeySaved && falKey === getProviderKey(ProviderKeys.FAL_LTX) ? (
                                        <span className="flex items-center gap-1.5">
                                            <CheckIcon className="w-3.5 h-3.5" />
                                            Connecté
                                        </span>
                                    ) : 'Sauvegarder la clé'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* GOOGLE DRIVE SECTION */}
            <div className="bg-black/20 border border-gray-700 rounded-xl p-6 relative overflow-hidden group mt-4">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <svg className="w-32 h-32 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12.01 1.485c-2.082 0-3.754.02-3.743.047.01.02 1.708 3.001 3.774 6.62l3.76 6.574h3.76c2.081 0 3.753-.02 3.742-.047-.01-.02-1.708-3.001-3.773-6.62l-3.76-6.574z" />
                        <path d="M4.48 7.01c-.95 1.65-1.71 3.01-1.7 3.03.02.02 7.5.03 8.5.01.02-.01-1.7-3.01-1.7-3.03-.01-.02-3.1-.01-5.1.01zM7.43 11.01c-2.08 0-3.75.02-3.74.05.02.02 1.71 3 3.77 6.62l3.76 6.57h7.52l-3.76-6.57c-2.07-3.62-3.76-6.59-3.77-6.62-.01-.03-1.7-.05-3.78-.05z" />
                    </svg>
                </div>

                <div className="flex items-start gap-4 relative z-10">
                    <div className="p-3 bg-blue-500/10 rounded-xl shrink-0 border border-blue-500/20">
                        <svg className="w-8 h-8 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12.01 1.485c-2.082 0-3.754.02-3.743.047.01.02 1.708 3.001 3.774 6.62l3.76 6.574h3.76c2.081 0 3.753-.02 3.742-.047-.01-.02-1.708-3.001-3.773-6.62l-3.76-6.574z" />
                            <path d="M4.48 7.01c-.95 1.65-1.71 3.01-1.7 3.03.02.02 7.5.03 8.5.01.02-.01-1.7-3.01-1.7-3.03-.01-.02-3.1-.01-5.1.01zM7.43 11.01c-2.08 0-3.75.02-3.74.05.02.02 1.71 3 3.77 6.62l3.76 6.57h7.52l-3.76-6.57c-2.07-3.62-3.76-6.59-3.77-6.62-.01-.03-1.7-.05-3.78-.05z" />
                        </svg>
                    </div>
                    <div className="flex-1 space-y-4">
                        <div>
                            <h4 className="font-bold text-white text-lg">Google Drive</h4>
                            <div className="flex items-center gap-2">
                                <p className="text-sm text-gray-400 mt-1">
                                    Stockez vos médias HD en sécurité sur votre Drive personnel.
                                </p>
                                <div className="group/tooltip relative mt-1">
                                    <div className="w-4 h-4 rounded-full border border-gray-500 text-gray-500 flex items-center justify-center text-[10px] cursor-help hover:text-white hover:border-white transition-colors">?</div>
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-gray-800 border border-gray-600 rounded-lg shadow-xl text-xs text-gray-300 opacity-0 group-hover/tooltip:opacity-100 pointer-events-none transition-opacity z-50">
                                        Studio Jenial utilise un accès restreint (drive.file) pour sécuriser uniquement les fichiers qu'il crée. Vos autres fichiers restent privés.
                                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 border-b border-r border-gray-600 rotate-45"></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4" data-tour="drive-section">
                            {driveLoading ? (
                                <div className="flex items-center gap-3 text-gray-400">
                                    <div className="w-4 h-4 border-2 border-gray-500 border-t-blue-400 rounded-full animate-spin"></div>
                                    Vérification de la connexion...
                                </div>
                            ) : driveConnected ? (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 text-green-400 text-sm font-medium">
                                        <CheckIcon className="w-4 h-4" />
                                        Compte Google Drive connecté
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-400 mb-2">Dossier de sauvegarde</label>

                                        {isFolderPickerOpen ? (
                                            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
                                                <DriveFolderPicker
                                                    initialFolderId={selectedDriveFolder || 'root'}
                                                    onSelect={(id, name) => {
                                                        setSelectedDriveFolder(id);
                                                        // Persist naming for display if needed, but ID is key
                                                        localStorage.setItem('jenial_drive_folder_id', id);
                                                        localStorage.setItem('jenial_drive_folder_name', name);
                                                        setIsFolderPickerOpen(false);
                                                    }}
                                                    onCancel={() => setIsFolderPickerOpen(false)}
                                                />
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-3">
                                                <div className="flex-1 bg-[#121212] border border-gray-600 rounded-lg px-4 py-3 flex items-center gap-3">
                                                    <FolderIcon className="w-5 h-5 text-blue-400" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm text-white font-medium truncate">
                                                            {localStorage.getItem('jenial_drive_folder_name') || 'Dossier par défaut (Studio Jenial)'}
                                                        </p>
                                                        <p className="text-[10px] text-gray-500 truncate">
                                                            {selectedDriveFolder ? `ID: ${selectedDriveFolder}` : 'Racine du Drive'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => setIsFolderPickerOpen(true)}
                                                    className="px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg border border-gray-600 text-sm font-medium transition-colors"
                                                >
                                                    Modifier
                                                </button>
                                            </div>
                                        )}

                                        <p className="text-[10px] text-gray-500 mt-2 flex items-center gap-1.5">
                                            <InfoIcon className="w-3 h-3" />
                                            Tous les médias générés seront sauvegardés dans ce dossier.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-2">
                                    <p className="text-sm text-gray-400 mb-3">Connectez votre Drive pour la sauvegarde HD automatique.</p>
                                    {userId ? (
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const { connectDrive } = await import('../services/googleDriveClient');
                                                    await connectDrive();
                                                } catch (error) {
                                                    console.error('[Settings] Drive connect error:', error);
                                                    alert(`Erreur: ${error instanceof Error ? error.message : 'Connexion Drive échouée'}`);
                                                }
                                            }}
                                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-bold transition-colors"
                                        >
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M12.01 1.485c-2.082 0-3.754.02-3.743.047.01.02 1.708 3.001 3.774 6.62l3.76 6.574h3.76c2.081 0 3.753-.02 3.742-.047-.01-.02-1.708-3.001-3.773-6.62l-3.76-6.574z" />
                                            </svg>
                                            Connecter Google Drive
                                        </button>
                                    ) : (
                                        <p className="text-yellow-500 text-xs">⚠️ Connectez-vous d'abord pour activer Drive</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

        </div>


    );

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[200] backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div
                className="bg-[#1e1e1e] rounded-2xl w-full max-w-4xl h-[70vh] flex overflow-hidden border border-gray-700 shadow-2xl relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-white hover:bg-gray-800 rounded-full p-2 transition-all z-10"
                >
                    <XMarkIcon className="w-6 h-6" />
                </button>

                {renderTabs()}

                {/* Content Area */}
                <div className="flex-1 bg-[#1e1e1e] p-10 overflow-y-auto custom-scrollbar relative">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-[0.02] pointer-events-none"
                        style={{ backgroundImage: 'radial-gradient(circle at 10px 10px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}>
                    </div>

                    <div className="relative z-10">
                        {activeTab === 'profile' && renderProfileContent()}
                        {activeTab === 'account' && renderAccountContent()}
                        {activeTab === 'integrations' && renderIntegrationsContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};
